// ============================================================================
// AUTHENTICATION ENDPOINTS
// ============================================================================
// Endpoint definitions for Authentication and Authorization module

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

/**
 * Base path for auth endpoints
 */
const BASE_PATH = '/auth';

/**
 * Auth Endpoints
 */
export const authEndpoints = {
  /**
   * User login
   * POST /auth/login
   */
  login: () => buildEndpoint(`${BASE_PATH}/login`),

  /**
   * User logout
   * POST /auth/logout
   */
  logout: () => buildEndpoint(`${BASE_PATH}/logout`),

  /**
   * Register new user
   * POST /auth/register
   */
  register: () => buildEndpoint(`${BASE_PATH}/register`),

  /**
   * Refresh access token
   * POST /auth/refresh
   */
  refresh: () => buildEndpoint(`${BASE_PATH}/refresh`),

  /**
   * Get current user
   * GET /auth/me
   */
  me: () => buildEndpoint(`${BASE_PATH}/me`),

  /**
   * Update current user profile
   * PUT /auth/me
   */
  updateProfile: () => buildEndpoint(`${BASE_PATH}/me`),

  /**
   * Change password
   * POST /auth/change-password
   */
  changePassword: () => buildEndpoint(`${BASE_PATH}/change-password`),

  /**
   * Forgot password
   * POST /auth/forgot-password
   */
  forgotPassword: () => buildEndpoint(`${BASE_PATH}/forgot-password`),

  /**
   * Reset password
   * POST /auth/reset-password
   */
  resetPassword: () => buildEndpoint(`${BASE_PATH}/reset-password`),

  /**
   * Verify email
   * POST /auth/verify-email
   */
  verifyEmail: () => buildEndpoint(`${BASE_PATH}/verify-email`),

  /**
   * Resend verification email
   * POST /auth/resend-verification
   */
  resendVerification: () => buildEndpoint(`${BASE_PATH}/resend-verification`),

  /**
   * Check permission
   * POST /auth/check-permission
   */
  checkPermission: () => buildEndpoint(`${BASE_PATH}/check-permission`),

  /**
   * Get user sessions
   * GET /auth/sessions
   */
  sessions: () => buildEndpoint(`${BASE_PATH}/sessions`),

  /**
   * Revoke session
   * DELETE /auth/sessions/:sessionId
   */
  revokeSession: (sessionId: string) =>
    buildEndpointWithParams(`${BASE_PATH}/sessions/:sessionId`, { sessionId }),

  // === Tenant-Hub Endpoints (RBAC integration) ===

  /**
   * Enterprise admin authentication (Keycloak-only, no tenant context)
   * POST /auth/enterprise-admin
   */
  enterpriseAdmin: () => buildEndpoint(`${BASE_PATH}/enterprise-admin`),

  /**
   * Discover accessible tenants/apps — Step 1 of two-step auth (for integrated apps)
   * POST /auth/discover
   */
  discover: () => buildEndpoint(`${BASE_PATH}/discover`),

  /**
   * Authenticate with selected tenant/app — Step 2 of two-step auth
   * POST /auth/authenticate
   */
  authenticate: () => buildEndpoint(`${BASE_PATH}/authenticate`),

  /**
   * Get user permission context
   * GET /auth/permissions?applicationId=
   */
  permissions: () => buildEndpoint(`${BASE_PATH}/permissions`),

  /**
   * Refresh user permissions (invalidate cache)
   * POST /auth/permissions/refresh?applicationId=
   */
  refreshPermissions: () => buildEndpoint(`${BASE_PATH}/permissions/refresh`),

  /**
   * Get UI details for a menu item (element-level permissions)
   * GET /auth/menu/:menuItemId/ui
   */
  menuUI: (menuItemId: string) =>
    buildEndpointWithParams(`${BASE_PATH}/menu/:menuItemId/ui`, { menuItemId }),
} as const;

/**
 * Authorization Endpoints (tenant-hub authorization checks)
 */
const AUTHORIZATION_BASE_PATH = '/authorization';

export const authorizationEndpoints = {
  /**
   * Check single permission
   * POST /authorization/check
   */
  check: () => buildEndpoint(`${AUTHORIZATION_BASE_PATH}/check`),

  /**
   * Batch check permissions
   * POST /authorization/batch-check
   */
  batchCheck: () => buildEndpoint(`${AUTHORIZATION_BASE_PATH}/batch-check`),
} as const;

/**
 * User Management Endpoints
 */
const USERS_BASE_PATH = '/users';

export const userEndpoints = {
  /**
   * Get all users
   * GET /users
   */
  list: () => buildEndpoint(USERS_BASE_PATH),

  /**
   * Get user by ID
   * GET /users/:id
   */
  getById: (id: ID) => buildEndpointWithParams(`${USERS_BASE_PATH}/:id`, { id }),

  /**
   * Create new user
   * POST /users
   */
  create: () => buildEndpoint(USERS_BASE_PATH),

  /**
   * Update user
   * PUT /users/:id
   */
  update: (id: ID) => buildEndpointWithParams(`${USERS_BASE_PATH}/:id`, { id }),

  /**
   * Delete user
   * DELETE /users/:id
   */
  delete: (id: ID) => buildEndpointWithParams(`${USERS_BASE_PATH}/:id`, { id }),

  /**
   * Search users
   * POST /users/search
   */
  search: () => buildEndpoint(`${USERS_BASE_PATH}/search`),

  /**
   * Get active users
   * GET /users/active
   */
  active: () => buildEndpoint(`${USERS_BASE_PATH}/active`),

  /**
   * Activate/Deactivate user
   * PATCH /users/:id/status
   */
  updateStatus: (id: ID) =>
    buildEndpointWithParams(`${USERS_BASE_PATH}/:id/status`, { id }),

  /**
   * Assign role to user
   * POST /users/:id/role
   */
  assignRole: (id: ID) =>
    buildEndpointWithParams(`${USERS_BASE_PATH}/:id/role`, { id }),

  /**
   * Assign permissions to user
   * POST /users/:id/permissions
   */
  assignPermissions: (id: ID) =>
    buildEndpointWithParams(`${USERS_BASE_PATH}/:id/permissions`, { id }),

  /**
   * Bulk delete users
   * DELETE /users/bulk
   */
  bulkDelete: () => buildEndpoint(`${USERS_BASE_PATH}/bulk`),
} as const;

/**
 * Export endpoints
 */
export default {
  auth: authEndpoints,
  authorization: authorizationEndpoints,
  users: userEndpoints,
};
